// hooks/useImageUpload.js
import { useCallback, useRef, useState } from "react";
import uploadFile from "../utils/uploadFile";

export default function useImageUpload({ getBasePath, userId, getParentPath }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const busy = useRef(false);

  // Accept native File/FileList values so existing file inputs keep working.
  const uploadImages = useCallback(
    async (selectedFiles, { fixedName = null } = {}) => {
      if (busy.current)
        throw new Error("An image upload is already in progress.");
      const selected = Array.from(selectedFiles || []);
      const result = { files: [], errors: [] };
      if (!selected.length) return result;
      const basePath = getBasePath?.();
      const parent = getParentPath?.();
      if (!userId || !basePath)
        throw new Error("The upload destination is not ready.");

      busy.current = true;
      setUploading(true);
      setProgress(0);

      try {
        for (let i = 0; i < selected.length; i++) {
          const file = selected[i];
          const count =
            selected.length > 1 ? ` ${i + 1} of ${selected.length}` : "";

          try {
            if (!file.type?.startsWith("image/"))
              throw new Error("Please select an image file.");

            const name = (file.name || "image").replace(
              /[^a-zA-Z0-9._-]/g,
              "_",
            );

            const id =
              window.crypto?.randomUUID?.() ||
              `${Date.now()}_${Math.random().toString(16).slice(2)}`;

            const safeFixedName = fixedName
              ? fixedName.replace(/[^a-zA-Z0-9._-]/g, "_")
              : null;

            setStatus(`Uploading photo${count}`);

            result.files.push(
              await uploadFile({
                file,
                fullPath: safeFixedName
                  ? `${basePath}/${safeFixedName}`
                  : `${basePath}/${id}_${name}`,
                userId,
                parent,
                onProgress: ({ progress: fraction }) => {
                  setProgress(
                    Math.round(((i + fraction) / selected.length) * 100),
                  );
                  if (fraction >= 1) setStatus(`Finishing photo${count}`);
                },
              }),
            );
          } catch (error) {
            result.errors.push({ file, error });
          }
        }

        return result;
      } finally {
        busy.current = false;
        setUploading(false);
        setProgress(0);
        setStatus("");
      }
    },
    [getBasePath, getParentPath, userId],
  );

  const uploadImage = useCallback(
    async (file, options = {}) => {
      if (!file) throw new Error("Choose an image first.");

      const result = await uploadImages([file], options);

      if (result.errors.length) throw result.errors[0].error;

      return result.files[0];
    },
    [uploadImages],
  );

  return { uploading, progress, status, uploadImage, uploadImages };
}
